import React, { FC, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Grid, Link } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import ClearIcon from '@material-ui/icons/Clear'
import DeleteDialog from '../ProductsSummary/DeleteDialog'
import { FormattedMessage } from 'react-intl'
import messages from '../../messages'
import clsx from 'clsx'
import { BiTubeProduct } from '../../products/BiTube/enum'
import { TubeProduct, TubeProperties } from '../../utils/enum'
import { configContext } from '../../utils/StandardReducer'
import { checkValidKey } from '../../utils/util'

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    minHeight: 325,
    overflow: 'hidden',
    '& .MuiDialogContent-root': {
      maxHeight: theme.spacing(102.5),
    },
    '& .MuiTableCell-root': {
      padding: theme.spacing(0, 3),
    },
    '& .MuiTableCell-root > div': {
      padding: theme.spacing(2.5, 0),
    },
    '& .MuiTableCell-head': {
      padding: theme.spacing(3),
    },
  },
  buttonWrapper: {
    justifyContent: 'flex-start',
    padding: theme.spacing(2, 6),
  },
  clearButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  table: {
    minWidth: 900,
  },
  title: {
    backgroundColor: theme.palette.background.grey,
  },
  rootRow: {
    color: theme.palette.grey[600],
    fontWeight: theme.typography.fontWeightMedium,
  },
  deleteAll: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
    margin: 0,
    padding: 0,
    '&:hover': {
      backgroundColor: 'inherit',
      color: theme.palette.primary.dark,
    },
  },
  delPopup: {
    height: 0,
  },
  nameWordWrap: {
    wordBreak: 'break-all',
    color: theme.palette.primary.main,
  },
  baseWidth: {
    width: 125,
  },
  seqWordWrap: {
    width: 290,
    wordBreak: 'break-all',
  },
  labelRoot: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.grey[500],
  },
  alignName: {
    marginLeft: theme.spacing(0.8),
  },
  deleteIcon: {
    fontSize: theme.typography.pxToRem(22),
    color: theme.palette.grey[400],
  },
  errorRow: {
    '& td:first-child > div': {
      borderLeft: `8px solid ${theme.palette.rich.red}`,
      marginLeft: theme.spacing(-4),
      padding: theme.spacing(6, 3.5),
    },
  },
  activeLabel: {},
}))

interface ViewAllDialogProps {
  dialogOpen: boolean
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  viewAllItems: any[]
  productName: string
  setActive: React.Dispatch<React.SetStateAction<any>>
  onDelete: any
}

let data
let closeViewAll = false

const ViewAllDialog: FC<ViewAllDialogProps> = (props) => {
  const classes = useStyles()
  const {
    dialogOpen,
    setDialogOpen,
    viewAllItems,
    setActive,
    productName,
    onDelete,
  } = props

  data = viewAllItems
  const { dispatch } = React.useContext(configContext)

  let isError = true
  const sequenceKey = messages.CONF_SEQUENCE_TEXT.defaultMessage

  // to check if fiveMod or threeMod contains mA,mC,mG,mU
  const list: any = ['mA', 'mC', 'mG', 'mU']
  const isTwoMeBase = (modification) => {
    return list.includes(modification)
  }

  const handleClose = () => {
    setDialogOpen(false)
    dispatch({
      type: 'IS_VIEW_ALL_CLICK',
      data: false,
    })
  }
  const productTypeHeader = () => {
    if (
      productName === BiTubeProduct.FastDuplex ||
      productName === BiTubeProduct.LightCycler ||
      productName === BiTubeProduct.Duplex
    ) {
      return <FormattedMessage {...messages.CONF_MY_DUPLEXES_SIDE_BAR} />
    } else if (
      productName === TubeProduct.Aqua ||
      productName === TubeProduct.Cpeptide
    ) {
      return <FormattedMessage {...messages.CONF_MY_OLIGOS_SIDE_BAR_aqua} />
    } else {
      return <FormattedMessage {...messages.CONF_MY_OLIGOS_SIDE_BAR} />
    }
  }

  const change = (id) => {
    setActive(id)
    handleClose()
  }

  const isValidRow = (row) => {
    isError = true
    if (row.listError) {
      row.listError.forEach((object) => {
        if (object.key === sequenceKey) {
          isError = false
        }
      })
    }
    return isError
  }
  // if all the elements are deleted close the pop up
  if (closeViewAll) {
    closeViewAll = false
    setDialogOpen(false)
    dispatch({
      type: 'IS_VIEW_ALL_CLICK',
      data: false,
    })
  }

  React.useEffect(() => {
    if (dialogOpen === true) {
      dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
      dispatch({
        type: 'IS_VIEW_ALL_CLICK',
        data: true,
      })
    }
    // TODO: need a test before we refactor these dependencies
  }, [dialogOpen])

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [delItem, setDelItem] = useState()
  const [delType, setDelType] = useState('')

  const renderData = () => {
    const result: any[] = []
    data.forEach((row, index) => {
      let scale = ''
      let amount = ''
      if (row.scale) scale = row.scale?.replace(/[^a-zA-Z0-9]/g, '_')
      if (row.amount) {
        amount = row.amount?.replace(/[^a-zA-Z0-9]/g, '_')
      }
      result.push(
        <TableRow
          key={index}
          className={
            row.listError
              ? row.listError.length !== 0
                ? classes.errorRow
                : ''
              : ''
          }
        >
          <TableCell>
            <div>
              {row.name ? (
                <Link
                  href="#"
                  underline="hover"
                  onClick={() => change(index)}
                  className={clsx(
                    classes.labelRoot && classes.activeLabel,
                    classes.nameWordWrap,
                    row.listError
                      ? row.listError.length === 0
                        ? classes.alignName
                        : ''
                      : ''
                  )}
                >
                  <strong>{row.name}</strong>
                </Link>
              ) : (
                '--'
              )}
            </div>
          </TableCell>
          <TableCell align="left" className={classes.seqWordWrap}>
            <div>
              {isValidRow(row) ? (
                <>
                  {productName === TubeProduct.Aqua ||
                  productName === TubeProduct.Cpeptide ? (
                    <>
                      {row.nterminalMod?.replace(/[[\]]/g, '') !==
                      TubeProperties.None
                        ? row.nterminalMod
                        : ''}

                      {row.sequence?.replace(/\s+/g, '')}

                      {row.cterminalMod?.replace(/[[\]]/g, '') !==
                      TubeProperties.None
                        ? row.cterminalMod
                        : ''}
                    </>
                  ) : (
                    <>
                      {/* Add fiveMod at the beginning */}
                      {row.fiveMod && isTwoMeBase(row.fiveMod)
                        ? `[${row.fiveMod}]`
                        : row.fiveMod?.replace(/[[\]]/g, '') !==
                            TubeProperties.None
                          ? row.fiveMod
                          : ''}

                      {
                        <>
                          {/* Display the cleaned sequence */}
                          {row.sequence?.replace(/\s+/g, '')}
                          {/* Add threeMod at the end */}
                          {row.threeMod && isTwoMeBase(row.threeMod)
                            ? `[${row.threeMod}]`
                            : row.threeMod?.replace(/[[\]]/g, '') !==
                                TubeProperties.None
                              ? row.threeMod
                              : ''}
                        </>
                      }
                    </>
                  )}
                </>
              ) : (
                '--'
              )}
            </div>
          </TableCell>
          <TableCell align="center">
            <div>{isValidRow(row) ? row.baseCount : '--'}</div>
          </TableCell>
          {productName === TubeProduct.EasyOligo ||
          productName === TubeProduct.PureSimple ||
          productName === TubeProduct.Beacon ||
          productName === TubeProduct.Probe ||
          productName === TubeProduct.Wellred ||
          productName === TubeProduct.iscaledna ||
          productName === TubeProduct.LongOligo ||
          productName === TubeProduct.Aqua ||
          productName === TubeProduct.Cpeptide ? (
            ' '
          ) : (
            <TableCell align="center">
              <div>
                {scale.length !== 0 ? (
                  <FormattedMessage
                    {...checkValidKey(
                      messages[`_PC_SPEC_${scale}`],
                      `_PC_SPEC_${scale}`
                    )}
                  />
                ) : (
                  '--'
                )}
              </div>
            </TableCell>
          )}

          {productName === TubeProduct.Beacon ||
          productName === TubeProduct.Wellred ||
          productName === TubeProduct.iscaledna ||
          productName === TubeProduct.Probe ? (
            <TableCell align="center">
              <div>
                {amount.length !== 0 ? (
                  <FormattedMessage
                    {...checkValidKey(
                      messages[`_PC_SPEC_amount_${amount}`],
                      `_PC_SPEC_amount_${amount}`
                    )}
                  />
                ) : (
                  '--'
                )}
              </div>
            </TableCell>
          ) : (
            ''
          )}
          {productName === TubeProduct.Aqua ||
          productName === TubeProduct.Cpeptide ? (
            <TableCell align="center">
              <div>
                {amount.length !== 0 ? (
                  <FormattedMessage
                    {...checkValidKey(
                      messages[`_PC_SPEC_${amount}`],
                      `_PC_SPEC_${amount}`
                    )}
                  />
                ) : (
                  '--'
                )}
              </div>
            </TableCell>
          ) : (
            ''
          )}

          {productName === TubeProduct.LongOligo ? (
            <TableCell align="center">
              <div>
                {amount.length !== 0 ? (
                  <FormattedMessage
                    {...checkValidKey(
                      messages[`PC_SPEC_${amount}`],
                      `PC_SPEC_${amount}`
                    )}
                  />
                ) : (
                  '--'
                )}
              </div>
            </TableCell>
          ) : (
            ''
          )}

          {productName === TubeProduct.EasyOligo ||
          productName === TubeProduct.PureSimple ||
          productName === TubeProduct.Aqua ||
          productName === TubeProduct.Cpeptide ? (
            ' '
          ) : (
            <TableCell align="center">
              <div>
                {row.purification?.length ? (
                  <FormattedMessage
                    {...checkValidKey(
                      messages[`_PC_SPEC_${row.purification}`],
                      `_PC_SPEC_${row.purification}`
                    )}
                  />
                ) : (
                  '--'
                )}
              </div>
            </TableCell>
          )}

          {productName === TubeProduct.Aqua ||
          productName === TubeProduct.Cpeptide ? (
            <TableCell align="center">
              <div>
                {row.purityLevel?.length !== 0 ? (
                  <>
                    {productName !== TubeProduct.Cpeptide ? (
                      <FormattedMessage
                        {...checkValidKey(
                          messages[`_PC_SPEC_${row.purityLevel}`],
                          `_PC_SPEC_${row.purityLevel}`
                        )}
                      />
                    ) : (
                      <FormattedMessage
                        {...checkValidKey(
                          messages[`PC_SPEC_${row.purityLevel}`],
                          `PC_SPEC_${row.purityLevel}`
                        )}
                      />
                    )}
                  </>
                ) : (
                  '--'
                )}
              </div>
            </TableCell>
          ) : (
            ''
          )}

          <TableCell align="center">
            <div>
              <IconButton
                aria-label="delete"
                onClick={() => {
                  setDeleteDialogOpen(true)
                  setDelItem(row)
                  setDelType('SelectedViewAll')
                }}
              >
                <DeleteIcon className={classes.deleteIcon} />
              </IconButton>
            </div>
          </TableCell>
        </TableRow>
      )
    })

    return result
  }
  return (
    <Dialog
      onClose={handleClose}
      aria-label="Are you sure you want to clear items?"
      open={dialogOpen}
      maxWidth="md"
      classes={{ paperScrollPaper: classes.dialog }}
    >
      <DialogTitle className={classes.title}>
        {productTypeHeader()} ({data.length})
      </DialogTitle>
      <IconButton
        className={classes.clearButton}
        color="primary"
        onClick={handleClose}
      >
        <ClearIcon />
      </IconButton>
      <DialogContent>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="left" className={classes.rootRow}>
                {productName === TubeProduct.Aqua ||
                productName === TubeProduct.Cpeptide ? (
                  <FormattedMessage {...messages.PEPTIDE_NAME} />
                ) : (
                  <FormattedMessage {...messages.CONF_OLIGO_NAME_TUBE} />
                )}
              </TableCell>
              <TableCell align="left" className={classes.rootRow}>
                <FormattedMessage {...messages.CONF_SEQUENCE_LABEL} />
              </TableCell>
              <TableCell
                align="center"
                className={clsx(classes.rootRow, classes.baseWidth)}
              >
                {productName === TubeProduct.Aqua ||
                productName === TubeProduct.Cpeptide ? (
                  <FormattedMessage {...messages.CONF_AMINO_ACIDS_VIEW_ALL} />
                ) : (
                  <FormattedMessage {...messages.CONF_BASES_VIEW_ALL} />
                )}
              </TableCell>
              {productName === TubeProduct.EasyOligo ||
              productName === TubeProduct.PureSimple ||
              productName === TubeProduct.Beacon ||
              productName === TubeProduct.Probe ||
              productName === TubeProduct.Wellred ||
              productName === TubeProduct.iscaledna ||
              productName === TubeProduct.LongOligo ||
              productName === TubeProduct.Aqua ||
              productName === TubeProduct.Cpeptide ? (
                ''
              ) : (
                <TableCell align="center" className={classes.rootRow}>
                  <FormattedMessage {...messages.CONF_VIEWALL_SCALE} />
                </TableCell>
              )}

              {productName === TubeProduct.Aqua ||
              productName === TubeProduct.Wellred ||
              productName === TubeProduct.iscaledna ||
              productName === TubeProduct.Probe ||
              productName === TubeProduct.Beacon ||
              productName === TubeProduct.LongOligo ||
              productName === TubeProduct.Cpeptide ? (
                <TableCell align="center" className={classes.rootRow}>
                  <FormattedMessage {...messages.AMOUNT} />
                </TableCell>
              ) : (
                ''
              )}

              {productName === TubeProduct.EasyOligo ||
              productName === TubeProduct.PureSimple ||
              productName === TubeProduct.Aqua ||
              productName === TubeProduct.Cpeptide ? (
                ' '
              ) : (
                <TableCell align="center" className={classes.rootRow}>
                  <FormattedMessage {...messages.CONF_PURIFICATION} />
                </TableCell>
              )}

              {productName === TubeProduct.Aqua ||
              productName === TubeProduct.Cpeptide ? (
                <TableCell align="center" className={classes.rootRow}>
                  <FormattedMessage {...messages._PC_SPEC_PURITY_LEVEL} />
                </TableCell>
              ) : (
                ''
              )}

              <TableCell align="left" className={classes.rootRow}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderData()}</TableBody>
        </Table>
      </DialogContent>
      <DialogActions className={classes.buttonWrapper}>
        <Grid container spacing={8}>
          <Grid item sm={10}>
            <div>
              <Button color="primary" variant="contained" onClick={handleClose}>
                Close
              </Button>
            </div>
          </Grid>

          <Grid item sm={2}>
            <div>
              <Button
                onClick={() => {
                  setDeleteDialogOpen(true)
                  setDelType('All')
                }}
                className={classes.deleteAll}
                id="modal-delete-all-link"
                data-testid="modal-delete-all-link"
              >
                Delete All
              </Button>
            </div>
          </Grid>
        </Grid>
      </DialogActions>
      <div className={classes.delPopup}>
        <DeleteDialog
          dialogOpen={deleteDialogOpen}
          setDialogOpen={setDeleteDialogOpen}
          selectedItem={delItem}
          type={delType}
          onDelete={onDelete}
        />
      </div>
    </Dialog>
  )
}

export default ViewAllDialog
